import { Command } from 'cmdk';
import {
  Plus,
  Search,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useShortcut } from '../../hooks/useKeyboardShortcuts';

const createItems = [
  { label: 'New Campaign', to: '/campaigns/new' },
];

const groupHeadingClass =
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-text-tertiary';

const itemClass =
  'flex cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-primary outline-none aria-selected:bg-brand-subtle aria-selected:text-brand-default data-[disabled]:pointer-events-none data-[disabled]:opacity-40 focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-1';

// ── CommandPalette ────────────────────────────────────────────

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const dialogId = React.useId();
  const titleId = `${dialogId}-title`;

  const handleSelect = (to: string) => {
    navigate(to);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[99] bg-black/40 backdrop-blur-sm animate-in fade-in"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Panel — role=dialog with combobox semantics via cmdk */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed left-1/2 top-[20vh] z-[100] w-[560px] -translate-x-1/2 overflow-hidden rounded-2xl border border-border-subtle bg-surface-overlay shadow-xl animate-in fade-in slide-in-from-bottom-4 duration-slow"
      >
        <h2 id={titleId} className="sr-only">Command palette — actions</h2>
        <Command
          className="flex flex-col"
          shouldFilter
          aria-label="Command palette"
          role="combobox"
          aria-expanded="true"
          aria-haspopup="listbox"
        >
          {/* Search bar */}
          <div className="flex items-center gap-2 border-b border-border-subtle px-3">
            <Search className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
            <Command.Input
              autoFocus
              placeholder="Search actions…"
              aria-label="Search actions"
              className="h-12 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-tertiary focus:outline-none"
            />
            <kbd
              className="hidden shrink-0 rounded border border-border-subtle bg-bg-muted px-1.5 py-0.5 text-xs text-text-tertiary sm:inline-block"
              aria-label="Press Escape to close"
            >
              Esc
            </kbd>
          </div>

          {/* Results */}
          <Command.List
            className="max-h-[380px] overflow-y-auto overscroll-contain p-2"
            aria-label="Command results"
            role="listbox"
          >
            <Command.Empty className="py-8 text-center text-sm text-text-tertiary" role="status">
              No results found.
            </Command.Empty>

            <Command.Group heading="Create" className={groupHeadingClass}>
              {createItems.map((item) => (
                <Command.Item
                  key={item.to}
                  value={item.label}
                  onSelect={() => handleSelect(item.to)}
                  className={itemClass}
                  role="option"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-brand-subtle text-brand-default" aria-hidden="true">
                    <Plus className="h-3.5 w-3.5" />
                  </span>
                  {item.label}
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </>
  );
}

// ── useCommandPalette hook ────────────────────────────────────

export function useCommandPalette() {
  const [open, setOpen] = React.useState(false);

  // cmd+k / ctrl+k opens the palette — handled via useShortcut
  useShortcut('cmd+k', () => setOpen((prev) => !prev));
  useShortcut('Escape', () => { if (open) setOpen(false); });

  return { open, setOpen };
}
